import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useEffect, useRef, useState } from 'react';
import { MaterialCircularProgress } from 'src/components/material/CircularProgress';
import styled from 'styled-components';
import VideoPlayer from '../VideoPlayer/VideoPlayer';

const List = styled.div`
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;

  padding: 0;
  margin: 0;
  scroll-snap-type: y mandatory;
  scrollbar-width: none; /* For Firefox */
  -ms-overflow-style: none; /* For Internet Explorer and Edge */
  box-sizing: border-box;

  &::-webkit-scrollbar {
    display: none; /* For Chrome, Safari, and Opera */
  }
`;

const Item = styled.div<{ isWideScreen: boolean }>`
  width: 100%;
  height: 100vh;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  display: flex;
  justify-content: center;
  padding-top: ${(props) => (props.isWideScreen ? '80px' : '112px')};
`;

interface ScrollContainerProps {
  posts: Array<any>;
  loadMorePosts: (page: number) => void;
  isLoading: boolean;
  hasMorePosts: boolean;
  currentPage: number;
  initialPostId?: string;
  onActivePostChange?: (post: any) => void;
}

const ScrollContainer: React.FC<ScrollContainerProps> = ({
  posts,
  loadMorePosts,
  isLoading,
  hasMorePosts,
  currentPage,
  initialPostId,
  onActivePostChange,
}) => {
  const videoObserver = useRef<IntersectionObserver | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const isWideScreen = useMediaQuery('(min-width:550px)');
  const requestedPages = useRef<Set<number>>(new Set());
  const listRef = useRef<HTMLDivElement>(null);
  const hasScrolledToInitial = useRef(false);

  // Scroll to the initial post when posts load
  useEffect(() => {
    if (initialPostId && posts.length > 0 && !hasScrolledToInitial.current) {
      const index = posts.findIndex((p) => p._id === initialPostId);
      if (index >= 0) {
        hasScrolledToInitial.current = true;
        setCurrentIndex(index);
        // Use requestAnimationFrame to ensure DOM is rendered
        requestAnimationFrame(() => {
          const items = listRef.current?.children;
          if (items && items[index]) {
            items[index].scrollIntoView({
              behavior: 'instant' as ScrollBehavior,
            });
          }
        });
      }
    }
  }, [initialPostId, posts]);

  useEffect(() => {
    if (onActivePostChange && posts[currentIndex]) {
      onActivePostChange(posts[currentIndex]);
    }
  }, [currentIndex, posts, onActivePostChange]);

  useEffect(() => {
    const container = listRef.current;
    if (!container) {
      return;
    }

    const videoElements = container.querySelectorAll<HTMLVideoElement>('video');

    const handleVideoIntersect = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const video = entry.target as HTMLVideoElement;
        const index = Number(video.dataset.index);

        if (!isNaN(index) && entry.isIntersecting) {
          video.play().catch(() => {});
          setCurrentIndex(index);
        } else {
          video.pause();
        }
      });
    };

    videoObserver.current = new IntersectionObserver(handleVideoIntersect, {
      threshold: 0.75,
    });

    videoElements.forEach((video) => videoObserver.current?.observe(video));

    return () => {
      videoElements.forEach((video) => videoObserver.current?.unobserve(video));
      videoObserver.current?.disconnect();
    };
  }, [posts]);

  useEffect(() => {
    if (
      currentIndex === posts.length - 2 &&
      hasMorePosts &&
      !isLoading &&
      !requestedPages.current.has(currentPage + 1)
    ) {
      loadMorePosts(currentPage + 1);
      requestedPages.current.add(currentPage + 1);
    }
  }, [
    currentIndex,
    posts.length,
    loadMorePosts,
    hasMorePosts,
    isLoading,
    currentPage,
  ]);

  return (
    <List ref={listRef}>
      {posts.map((post, index) => (
        <Item key={post._id ?? index} isWideScreen={isWideScreen}>
          <VideoPlayer
            post={post}
            videoUrl={post.video_url}
            index={index}
            active={index === currentIndex}
          />
        </Item>
      ))}
      {isLoading && <MaterialCircularProgress />}
    </List>
  );
};

export default ScrollContainer;
